import { Building, CircleCheck, Globe, Send } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';

// banner
import Banner from './lsb_banner.png';

const backendUrl = "https://portal.lsb.go.ke/account/verify/";
const MEDIA_ROOT = "https://portal.lsb.go.ke";

// Utility for the zigzag pattern CSS
const zigzagBackground =
  `repeating-linear-gradient(0deg, transparent, transparent 20px, rgba(75,85,99,0.08) 20px, rgba(75,85,99,0.08) 21px),
   repeating-linear-gradient(90deg, transparent, transparent 30px, rgba(107,114,128,0.06) 30px, rgba(107,114,128,0.06) 31px),
   repeating-linear-gradient(60deg, transparent, transparent 40px, rgba(55,65,81,0.05) 40px, rgba(55,65,81,0.05) 41px),
   repeating-linear-gradient(150deg, transparent, transparent 35px, rgba(31,41,55,0.04) 35px, rgba(31,41,55,0.04) 36px)`;

function PublicProfile() {
  const { licenseNumber } = useParams(); // Getting the license number from the URL
  const [surveyor, setSurveyor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref used for scrolling to top element
  const topRef = useRef(null);

  // Always scroll to the top when this page loads or the license number changes
  useEffect(() => {
    if (topRef.current) {
      // Use smooth scroll if supported, fallback otherwise
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [licenseNumber]);

  useEffect(() => {
    async function fetchSurveyor() {
      // If backendUrl already ends with /, we don't need another one.
      const baseUrl = backendUrl.endsWith('/') ? backendUrl : `${backendUrl}/`;
      const url = `${baseUrl}${licenseNumber}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch surveyor details: ${response.status}`);
        }
        const data = await response.json();
        setSurveyor(data);
      } catch (error) {
        console.error('Error fetching surveyor details:', error);
        setError('Failed to fetch surveyor details. The surveyor might not exist or the link is invalid.');
      } finally {
        setLoading(false);
      }
    }

    fetchSurveyor();
  }, [licenseNumber]);

  if (loading) {
    return (
      <div ref={topRef} className="flex flex-col justify-center items-center min-h-[400px] gap-4" style={{ backgroundImage: zigzagBackground }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="text-gray-500 font-medium">Loading surveyor profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div ref={topRef} className="max-w-4xl mx-auto p-6 text-center mt-10" style={{ backgroundImage: zigzagBackground }}>
        <div className="bg-red-50 p-8 rounded-2xl border border-red-100 shadow-sm">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 15.31c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Surveyor Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/register" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors">
            Back to Surveyor Register
          </Link>
        </div>
      </div>
    );
  }

  // handle image
  // ✅ Construct profile image URL dynamically
  const imagePath = surveyor.url || surveyor.profile_pic || "";
  let profilePicUrl = "";

  if (imagePath) {
    if (/^https?:\/\//i.test(imagePath)) {
      // Already a full URL
      profilePicUrl = imagePath;
    } else {
      // Relative media path — prepend server root
      profilePicUrl = `${MEDIA_ROOT}${imagePath.startsWith("/") ? "" : "/"}${imagePath}`;
    }
  } else {
    // Fallback: generate from initials
    profilePicUrl = `https://ui-avatars.com/api/?background=8d6b0b&color=fff&name=${encodeURIComponent(
      surveyor.name || "Surveyor"
    )}`;
  }

  // --- Status conditional styling ---
  let statusText = surveyor.status || 'Unknown Status';
  let statusLower = statusText?.toLowerCase().trim();
  let statusClass =
    statusLower === 'active'
      ? 'bg-[#3d0707] text-white border-[#ebcab3]'
      : 'bg-[#ebcab3] text-[#3d0707] border-[#3d0707]';

  // LSB Verification box: Message content based on status
  function getLsbVerificationMessage() {
    // Defensive matching for the known statuses:
    if (statusLower === "active") {
      return (
        <p className="text-sm text-start text-[#3d0707] font-medium">
          This surveyor is officially <strong>active</strong> and registered with the Land Surveyors Board of Kenya. This profile serves as a public verification of their current licensing status and professional standing.
        </p>
      );
    } else if (statusLower === "not active" || statusLower === "inactive") {
      return (
        <p className="text-sm text-start text-[#3d0707] font-medium">
          Registered with the Land Surveyors Board of Kenya but currently <strong>not actively practising</strong>. Please contact the Board for further information regarding their eligibility to practice.
        </p>
      );
    } else if (statusLower === "government surveyor" || statusLower === "govt surveyor" || statusLower === "government") {
      return (
        <p className="text-sm text-start text-[#3d0707] font-medium">
          Serves as a Government Surveyor, officially recognized and registered by the Land Surveyors Board of Kenya.
        </p>
      );
    } else {
      // Fallback/general message
      return (
        <p className="text-sm text-start text-[#3d0707] font-medium">
          This surveyor is registered with the Land Surveyors Board of Kenya. Please review the licensing status above for more context.
        </p>
      );
    }
  }

  return (
    <div
      ref={topRef}
      className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 relative rounded-2xl"
    // style={{ backgroundImage: zigzagBackground, backgroundColor: "#fff" }}
    >
      <Link to="/register" className="mb-4">
        <button className="button mt-4">
          <span className="button_lg">
            <span className="button_sl"></span>
            <span className="button_text" style={{ fontSize: 'calc(.5em + .3vw)' }}>back to register</span>
          </span>
        </button>
      </Link>

      {surveyor ? (
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 transition-all hover:shadow-2xl cursor-pointer relative">
          {/* Zigzag Lightning - Light Pattern Overlay */}
          <div
            className="absolute inset-0 z-0 pointer-events-none"
            style={{
              backgroundImage: zigzagBackground,
              opacity: 1,
            }}
          />
          {/* Header section with profile background */}
          <div className="h-40 bg-[#ebcab3] relative">
            <img src={Banner} alt="Banner" className="w-full h-full object-cover" />
          </div>

          <div className="px-6 pb-10 relative z-10">
            <div className="relative -mt-20 mb-6 flex flex-col sm:flex-row justify-between items-center sm:items-end gap-4">
              <div className="p-1 rounded-full shadow-lg">
                <div className="h-32 w-32 bg-gray-50 rounded-full flex items-center justify-center border-4 border-[#3d0707] overflow-hidden shadow-inner">
                  <img
                    src={profilePicUrl}
                    alt={surveyor.name || "Surveyor Photo"}
                    className="rounded-full"
                    style={{ objectPosition: "top" }}
                  />
                </div>
              </div>
              <div className="mb-2">
                <span className={`px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest border-2 shadow-sm ${statusClass}`}>
                  {statusText}
                </span>
              </div>
            </div>

            <div className="text-center sm:text-left">
              <h3 className="font-extrabold uppercase text-gray-900 mb-2 tracking-tight">{surveyor.name}</h3>
              <div className="inline-flex items-center px-3 py-1 bg-[#ebcab3] rounded-lg border border-[#3d0707]">
                <span className="text-[#3d0707] font-bold tracking-tight">License No: {surveyor.license_no}</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                <div className="space-y-2">
                  <span className="font-black text-gray-500 uppercase tracking-[0.2em]">Professional Information</span>

                  <div className="group flex items-center px-4 py-2 rounded-xl bg-gray-50 border border-gray-100 transition-colors hover:bg-white hover:border-blue-100 hover:shadow-sm">
                    <div className='bg-[#3d0707] p-2.5 rounded-lg mr-4 shadow-[#ebcab3] shadow-md'>
                      <Building className="w-5 h-5 text-white mt-0.5" />
                    </div>

                    <div className='text-start'>
                      <small className="text-gray-500 font-medium uppercase tracking-wider">Firm / Organization</small>
                      <p className="text-gray-900 font-bold leading-tight">{surveyor.firm || 'Private Practitioner'}</p>
                    </div>
                  </div>

                  <div className="group flex items-center px-4 py-2 rounded-xl bg-gray-50 border border-gray-100 transition-colors hover:bg-white hover:border-blue-100 hover:shadow-sm">
                    <div className="mt-0.5 bg-[#3d0707] p-2.5 rounded-lg mr-4 shadow-[#ebcab3] shadow-md">
                      <Send className="w-5 h-5 text-white" />
                    </div>

                    <div className='text-start'>
                      <small className="text-gray-500 font-medium uppercase tracking-wider">Postal Address</small>
                      <p className="text-gray-900 font-bold leading-tight">{surveyor.postal_address || 'Not Provided'}</p>
                    </div>
                  </div>

                  <div className="group flex items-center px-4 py-2 rounded-xl bg-gray-50 border border-gray-100 transition-colors hover:bg-white hover:border-blue-100 hover:shadow-sm">
                    <div className="mt-0.5 bg-[#3d0707] p-2.5 rounded-lg mr-4 shadow-[#ebcab3] shadow-md">
                      <Globe className="w-5 h-5 text-white" />
                    </div>

                    <div className='text-start'>
                      <small className="text-gray-500 font-medium uppercase tracking-wider">Website</small>
                      <p className="text-gray-900 font-bold leading-tight">{surveyor.website || 'Not Provided'}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="font-black text-gray-500 uppercase tracking-[0.2em]">LSB Verification</span>
                  <div className="bg-[#ebcab3] p-6 rounded-2xl border border-blue-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 -mt-9 -mr-5 h-24 w-24 bg-gray-100 rounded-full opacity-80"></div>
                    <div className="relative z-10">
                      <div className="flex text-[#3d0707] font-black mb-3">
                        <div className="bg-[#3d0707] p-1 rounded-full mr-2">
                          <CircleCheck className="w-5 h-5 text-white" />
                        </div>
                        OFFICIALLY VERIFIED
                      </div>
                      {getLsbVerificationMessage()}

                      <p className="text-[10px] font-black text-[#3d0707] uppercase tracking-widest text-center border-t border-[#3d0707] border-dashed pt-3">Status updated: {new Date().getFullYear()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200"
          style={{ backgroundImage: zigzagBackground }}
        >
          <p className="text-gray-400 text-xl font-medium">No details found for this surveyor.</p>
        </div>
      )}
    </div>
  );
}

export default PublicProfile;
